import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

/** DataTable's row heights (`rowHeight`), by the same height utilities. */
const ROW_HEIGHT = {
  sm: 'h-table-row-compact',
  md: 'h-table-row',
  lg: 'h-table-row-two-line',
  xl: 'h-table-row-mark',
} as const

interface DataTableSkeletonProps {
  /** Rows to hold: the table's page size, or its typical count when it is unpaged. */
  rows: number
  /** Columns, the lead one included. Every column after the lead is a right-aligned figure. */
  columns: number
  rowHeight?: keyof typeof ROW_HEIGHT
  /** DataTable's `bare`: no panel (border, ground, inset, head band), for a table inside a card's
   *  own. */
  bare?: boolean
  className?: string | undefined
}

/**
 * The loading shape of a DataTable, read off its own classes so the table does not move when the
 * rows land: the bordered `@container/table` panel on `bg-card`, its 16px side inset, the
 * `h-table-head` head band run to the panel's edge, and body rows at the table's row height,
 * each ruled but the last. Each body row is one bar, as the loaded rows are one line each.
 *
 * Its own light module (Skeleton and `cn` only): a route's `pendingComponent` ships in the entry
 * chunk, so it may not pull DataTable's component module in.
 */
export default function DataTableSkeleton({
  rows,
  columns,
  rowHeight = 'md',
  bare = false,
  className,
}: DataTableSkeletonProps) {
  const figures = Array.from({ length: Math.max(columns - 1, 0) }, (_, index) => index)
  const inset = bare ? null : 'px-4'

  return (
    <div
      aria-hidden="true"
      // DataTable's own marker: a frame steps back around a panel, loaded or not.
      {...(bare ? {} : { 'data-table-panel': '' })}
      className={cn(
        '@container/table w-full overflow-hidden',
        bare ? null : 'border border-border bg-card',
        className,
      )}
    >
      {/* The head runs the panel's full width, as the loaded table's band does, and its label
          bars sit on the same inset as the rows. */}
      <div
        className={cn(
          'flex h-table-head items-center gap-4 border-b',
          bare ? 'border-border-subtle' : 'border-border bg-table-head',
          inset,
        )}
      >
        <Skeleton className="h-3 w-16" />
        <div className="ml-auto flex items-center gap-8">
          {figures.map((cell) => (
            <Skeleton key={cell} className="h-3 w-12" />
          ))}
        </div>
      </div>
      {/* The rows' hairlines stop at the inset, as the loaded rows' do. */}
      <div className={cn(inset)}>
        {Array.from({ length: rows }, (_, row) => (
          <div
            key={row}
            className={cn(
              'flex items-center border-b border-border-subtle last:border-b-0',
              ROW_HEIGHT[rowHeight],
            )}
          >
            <Skeleton className="h-4 w-full" />
          </div>
        ))}
      </div>
    </div>
  )
}
